class GlobalQuickOrder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.searchInput = this.querySelector('[data-global-quick-order-search]');
    this.status = this.querySelector('[data-global-quick-order-status]');
    this.resultsPanel = this.querySelector('[data-global-quick-order-results-panel]');
    this.results = this.querySelector('[data-global-quick-order-results]');
    this.importInput = this.querySelector('[data-global-quick-order-import-input]');
    this.importReviewButton = this.querySelector('[data-global-quick-order-import-review]');
    this.importClearButton = this.querySelector('[data-global-quick-order-import-clear]');
    this.csvInput = this.querySelector('[data-global-quick-order-csv-input]');
    this.fileTrigger = this.querySelector('[data-global-quick-order-file-trigger]');
    this.csvMeta = this.querySelector('[data-global-quick-order-csv-meta]');
    this.csvReviewButton = this.querySelector('[data-global-quick-order-csv-review]');
    this.csvRemoveButton = this.querySelector('[data-global-quick-order-csv-remove]');
    this.templateXlsxButton = this.querySelector('[data-global-quick-order-template-xlsx]');
    this.templateCsvButton = this.querySelector('[data-global-quick-order-template-csv]');
    this.importStatus = this.querySelector('[data-global-quick-order-import-status]');
    this.importReviewPanel = this.querySelector('[data-global-quick-order-import-review-panel]');
    this.importSource = this.querySelector('[data-global-quick-order-import-source]');
    this.importSummary = this.querySelector('[data-global-quick-order-import-summary]');
    this.importResults = this.querySelector('[data-global-quick-order-import-results]');
    this.importAddButton = this.querySelector('[data-global-quick-order-import-add]');
    this.entryTabs = Array.from(this.querySelectorAll('[data-global-quick-order-entry-tab]'));
    this.entryPanels = Array.from(this.querySelectorAll('[data-global-quick-order-entry-panel]'));
    this.selected = this.querySelector('[data-global-quick-order-selected]');
    this.emptyState = this.querySelector('[data-global-quick-order-empty]');
    this.skuCount = this.querySelector('[data-global-quick-order-sku-count]');
    this.unitCount = this.querySelector('[data-global-quick-order-unit-count]');
    this.subtotal = this.querySelector('[data-global-quick-order-subtotal]');
    this.addAllButton = this.querySelector('[data-global-quick-order-add-all]');
    this.successActions = this.querySelector('[data-global-quick-order-success-actions]');
    this.entryControls = this.querySelector('.global-quick-order__search-card');
    this.cartStatus = this.querySelector('[data-global-quick-order-cart-status]');
    this.cartReviewActions = this.querySelector('[data-global-quick-order-cart-review]');
    this.cartReviewDetails = this.querySelector('[data-global-quick-order-cart-details]');
    this.cartRecheckButton = this.querySelector('[data-global-quick-order-cart-recheck]');
    this.cartReviewedButton = this.querySelector('[data-global-quick-order-cart-reviewed]');
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.xlsxScriptUrl = this.dataset.xlsxScriptUrl || '';
    this.selectedVariantIds = new Set();
    this.searchCache = new Map();
    this.productMetadataCache = new Map();
    this.identifierResolutionCache = new Map();
    this.rowHtmlCache = new Map();
    this.importReviewRows = [];
    this.importProcessedCount = 0;
    this.importSourceLabel = '';
    this.csvFile = null;
    this.csvFileText = '';
    this.csvFileRows = [];
    this.csvFileType = '';
    this.csvSheetName = '';
    this.csvDetectedRows = 0;
    this.importMaxRows = 100;
    this.importConcurrency = 4;
    this.csvMaxFileSize = 1024 * 1024;
    this.xlsxMaxFileSize = 5 * 1024 * 1024;
    this.xlsxLoadPromise = null;
    this.searchAbortController = null;
    this.productAbortController = null;
    this.rowAbortController = null;
    this.isAddingToCart = false;
    this.isImporting = false;
    this.isReadingFile = false;
    this.cartOutcomePending = false;
    this.cartAttempt = null;
    this.selectionEpoch = 0;
    this.pendingVariantRequests = new Map();
    this.lockedControls = new Map();

    this.searchEpoch = 0;
    this.productChoiceEpoch = 0;
    this.importEpoch = 0;
    this.fileEpoch = 0;
    const runSearch = this.debounce(() => this.handleSearch(), 300);
    this.searchInput?.addEventListener('input', () => {
      if (this.isSelectionLocked()) return;
      this.invalidateSearch();
      this.setStatus('');
      runSearch();
    });
    this.importInput?.addEventListener('input', () => {
      if (this.isSelectionLocked()) return;
      this.cancelImportWork();
      this.setImportStatus('Input changed. Review the current rows again.', 'info');
    });
    this.addAllButton?.addEventListener('click', () => this.addAllToCart());
    this.cartRecheckButton?.addEventListener('click', () => this.recheckCartOutcome());
    this.cartReviewedButton?.addEventListener('click', () => this.clearReviewedSubmission());
    this.importReviewButton?.addEventListener('click', () => this.handleImportReview());
    this.importClearButton?.addEventListener('click', () => this.clearImport());
    this.importAddButton?.addEventListener('click', () => this.addImportRowsToOrder());
    this.fileTrigger?.addEventListener('click', () => this.csvInput?.click());
    this.csvInput?.addEventListener('change', (event) => this.handleCsvFileSelection(event));
    this.csvReviewButton?.addEventListener('click', () => this.handleCsvImportReview());
    this.csvRemoveButton?.addEventListener('click', () => this.clearCsvFile());
    this.templateXlsxButton?.addEventListener('click', () => this.downloadXlsxTemplate());
    this.templateCsvButton?.addEventListener('click', () => this.downloadCsvTemplate());
    this.entryTabs.forEach((tab) => {
      tab.addEventListener('click', () => this.setActiveEntryTab(tab.dataset.globalQuickOrderEntryTab));
      tab.addEventListener('keydown', (event) => this.handleEntryTabKeydown(event));
    });
    this.setActiveEntryTab('search', { focus: false });
    this.contextEpoch = 0;
    this.contextKey = '';
    this.contextReady = false;
    this.contextChecking = false;
    this.cartForRules = null;
    this.contextVerifyButton = this.querySelector('[data-gqo-verify]');
    this.contextVerifyButton.addEventListener('click', () => this.verifySelectionContext());
    this.querySelector('[data-gqo-discard]').addEventListener('click', () => {
      if (this.isAddingToCart || this.cartOutcomePending || this.contextChecking) return;
      this.clearSelectedItems();
      this.verifySelectionContext();
    });
    this.onContextResume = () => {
      if (!document.hidden) this.verifySelectionContext();
      else this.invalidateContext('Verify context after returning to this page.');
    };
    window.addEventListener('pageshow', this.onContextResume);
    window.addEventListener('focus', this.onContextResume);
    document.addEventListener('visibilitychange', this.onContextResume);
    // Location cookies changed in another tab do not emit a storage event.
    this.contextTimer = window.setInterval(() => this.monitorContext(), 10000);
    this.contextReadyPromise = this.verifySelectionContext();
    this.updateSummary();
  }

  setActiveEntryTab(tabName, options = {}) {
    if (this.isSelectionLocked()) return;
    const focus = options.focus !== false;

    this.entryTabs.forEach((tab) => {
      const isActive = tab.dataset.globalQuickOrderEntryTab === tabName;

      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;

      if (isActive && focus) {
        tab.focus({ preventScroll: true });
      }
    });

    this.entryPanels.forEach((panel) => {
      const isActive = panel.dataset.globalQuickOrderEntryPanel === tabName;

      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
    });
  }

  handleEntryTabKeydown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (!keys.includes(event.key) || this.entryTabs.length === 0) {
      return;
    }

    event.preventDefault();

    const currentIndex = this.entryTabs.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.entryTabs.length - 1;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = currentIndex <= 0 ? this.entryTabs.length - 1 : currentIndex - 1;
    } else if (event.key === 'ArrowRight') {
      nextIndex = currentIndex >= this.entryTabs.length - 1 ? 0 : currentIndex + 1;
    }

    const nextTab = this.entryTabs[nextIndex];

    if (nextTab) {
      this.setActiveEntryTab(nextTab.dataset.globalQuickOrderEntryTab);
    }
  }

  async handleSearch() {
    if (this.isSelectionLocked()) return;
    this.invalidateSearch();
    const request = this.searchEpoch;
    const epoch = this.selectionEpoch;
    const query = this.searchInput.value.trim();
    const current = () => request === this.searchEpoch && this.selectionRequestIsCurrent(epoch);
    if (query.length < 2) { this.setStatus(query ? 'Enter at least 2 characters.' : ''); return; }
    this.setStatus('Searching...');
    this.showResultsPanel();
    try {
      const products = await this.searchProducts(query);
      if (!current()) return;
      this.renderSearchResults(products, query);
      this.setStatus(products.length ? products.length + ' results found.' : 'No search results in the current catalog.');
    } catch (error) {
      if (!current() || error.name === 'AbortError') return;
      const detail = this.classifyImportError(error);
      this.setStatus(detail.status + ': ' + detail.note);
    }
  }

  async searchProducts(query, options = {}) {
    const { abortPrevious = true, signal = null } = options;
    const epoch = this.contextEpoch;
    const url = this.buildPredictiveSearchUrl(query);
    const cacheKey = this.contextKey + ':' + epoch + ':' + url.toString();
    if (abortPrevious) {
      this.searchAbortController?.abort();
      this.searchAbortController = new AbortController();
    }
    if (this.searchCache.has(cacheKey)) return this.searchCache.get(cacheKey);
    const data = await this.fetchLookup(url, { signal: signal || (abortPrevious ? this.searchAbortController.signal : null) });
    if (epoch !== this.contextEpoch) throw this.requestError('cancelled', 'Customer context changed.');
    const products = data?.resources?.results?.products;
    if (!Array.isArray(products)) throw this.requestError('format', 'Search response is missing its product list.');
    this.searchCache.set(cacheKey, products);
    return products;
  }

  buildPredictiveSearchUrl(query) {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}search/suggest.json`, window.location.origin);

    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,variants.title,variants.sku,variants.barcode');

    return url;
  }

  renderSearchResults(products, query) {
    if (this.isSelectionLocked()) return;
    this.clearResults();

    if (products.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'global-quick-order__no-results';
      empty.textContent = 'No products found.';
      this.results.appendChild(empty);
      this.showResultsPanel();
      return;
    }

    this.showResultsPanel();

    const list = document.createElement('ul');
    list.className = 'global-quick-order-results';

    products.forEach((product) => {
      const handle = this.getProductHandle(product);
      const item = document.createElement('li');
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'global-quick-order-result';
      button.dataset.productHandle = handle;

      const imageUrl = product.featured_image?.url || product.image || product.featured_image;

      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = product.title || '';
        image.loading = 'lazy';
        button.appendChild(image);
      }

      const text = document.createElement('span');
      const title = document.createElement('strong');

      title.textContent = product.title || 'Untitled product';
      text.append(title);
      button.appendChild(text);

      button.addEventListener('click', () => this.selectProduct(product, query));
      item.appendChild(button);
      list.appendChild(item);
    });

    this.results.appendChild(list);
  }

  async selectProduct(product, query) {
    if (this.isSelectionLocked()) return;
    const epoch = this.selectionEpoch;
    const choice = ++this.productChoiceEpoch;
    const handle = this.getProductHandle(product);

    if (!handle) {
      this.setStatus('Product metadata is missing a handle.');
      return;
    }

    this.setStatus('Loading product variants...');

    try {
      const productJson = await this.fetchProductMetadata(handle);
      if (!this.selectionRequestIsCurrent(epoch) || choice !== this.productChoiceEpoch) return;
      productJson.handle = productJson.handle || handle;

      const variants = productJson.variants || [];

      if (variants.length === 0) {
        this.setStatus('Product Ajax metadata did not return variants.');
        return;
      }

      const exactMatches = this.findExactSkuOrBarcodeMatches(variants, query);

      if (exactMatches.length === 1) {
        this.selectVariant(productJson, exactMatches[0]);
        return;
      }

      if (variants.length === 1) {
        this.selectVariant(productJson, variants[0]);
        return;
      }

      this.renderVariantChoices(productJson, exactMatches.length > 1 ? exactMatches : variants, exactMatches.length > 1);
      this.setStatus(exactMatches.length > 1 ? 'Multiple exact SKU/barcode matches found. Select a variant.' : 'Select a variant.');
    } catch (error) {
      if (error.name === 'AbortError' || !this.selectionRequestIsCurrent(epoch) || choice !== this.productChoiceEpoch) return;
      const detail = this.classifyImportError(error);
      this.setStatus(detail.status + ': ' + detail.note);
    }
  }

  async fetchProductMetadata(handle, options = {}) {
    const { abortPrevious = true, signal = null } = options;
    const epoch = this.contextEpoch;
    const cacheKey = this.contextKey + ':' + epoch + ':' + String(handle);
    if (this.productMetadataCache.has(cacheKey)) return this.productMetadataCache.get(cacheKey);
    if (abortPrevious) {
      this.productAbortController?.abort();
      this.productAbortController = new AbortController();
    }
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(root + 'products/' + encodeURIComponent(handle) + '.js', window.location.origin);
    const request = this.fetchLookup(url, { notFound: true, signal: signal || (abortPrevious ? this.productAbortController.signal : null) })
      .then(product => {
        if (epoch !== this.contextEpoch) throw this.requestError('cancelled', 'Customer context changed.');
        if (!Array.isArray(product?.variants)) throw this.requestError('format', 'Product response is missing variants.');
        product.handle = product.handle || handle;
        return product;
      }).catch(error => { this.productMetadataCache.delete(cacheKey); throw error; });
    this.productMetadataCache.set(cacheKey, request);
    return request;
  }

  renderVariantChoices(product, variants, exactOnly = false) {
    if (this.isSelectionLocked()) return;
    this.clearResults();

    const wrapper = document.createElement('div');
    wrapper.className = 'global-quick-order-variants';

    const heading = document.createElement('h3');
    heading.textContent = exactOnly ? 'Matching variants' : 'Select a variant';
    wrapper.appendChild(heading);

    const productTitle = document.createElement('p');
    productTitle.className = 'global-quick-order-variants__product';
    productTitle.textContent = product.title;
    wrapper.appendChild(productTitle);

    const list = document.createElement('ul');

    variants.forEach((variant) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      const content = document.createElement('span');
      const title = document.createElement('strong');
      const meta = document.createElement('small');
      const action = document.createElement('span');
      const metaParts = [];

      if (variant.sku) {
        metaParts.push(`SKU: ${variant.sku}`);
      }

      if (variant.barcode) {
        metaParts.push(`Barcode: ${variant.barcode}`);
      }

      metaParts.push(variant.available ? 'Available' : 'Unavailable');

      button.type = 'button';
      button.className = 'global-quick-order-variant';
      button.disabled = !variant.available;
      title.textContent = variant.title;
      meta.textContent = metaParts.join(' · ');
      action.className = 'global-quick-order-variant__action';
      action.textContent = 'Select';

      content.append(title, meta);
      button.append(content, action);
      button.addEventListener('click', () => this.selectVariant(product, variant));

      if (!variant.available) {
        button.title = 'Unavailable variant';
      }

      item.appendChild(button);
      list.appendChild(item);
    });

    wrapper.appendChild(list);
    this.results.appendChild(wrapper);
    this.showResultsPanel();
  }

  async selectVariant(product, variant) {
    if (this.isSelectionLocked()) return;
    const variantId = String(variant.id);
    const epoch = this.selectionEpoch;
    if (!variant.available) {
      this.setStatus('Unavailable variant.');
      return;
    }
    if (this.selectedVariantIds.has(variantId)) {
      this.focusExistingRow(variantId);
      this.setStatus('That variant is already selected.');
      return;
    }
    if (this.pendingVariantRequests.has(variantId)) return;
    const request = {};
    this.pendingVariantRequests.set(variantId, request);
    this.setStatus('Fetching server-rendered B2B row...');
    try {
      const row = await this.fetchServerRenderedRow(product.handle, variantId, { abortPrevious: false });
      if (!this.selectionRequestIsCurrent(epoch)) return;
      if (row.dataset.hasProduct !== 'true' || String(row.dataset.variantId) !== variantId) {
        this.setStatus('Section Rendering returned an unexpected variant.');
        return;
      }
      if (row.dataset.available !== 'true') { this.setStatus('Unavailable variant in the current catalog.'); return; }
      if (!this.attachRow(row, variantId)) return;
      this.hideSuccessActions();
      this.updateSummary();
      this.setStatus('Server-rendered B2B row added.');
    } catch (error) {
      if (error.name !== 'AbortError' && this.selectionRequestIsCurrent(epoch)) {
        const detail = this.classifyImportError(error);
        this.setStatus(detail.status + ': ' + detail.note);
      }
    } finally {
      if (this.pendingVariantRequests.get(variantId) === request) this.pendingVariantRequests.delete(variantId);
    }
  }

  async fetchServerRenderedRow(handle, variantId, options = {}) {
    const { abortPrevious = true, signal = null } = options;
    const epoch = this.contextEpoch;
    const key = this.contextKey;
    const cacheKey = key + ':' + epoch + ':' + handle + ':' + variantId;

    if (this.rowHtmlCache.has(cacheKey)) {
      return this.parseServerRenderedRow(this.rowHtmlCache.get(cacheKey));
    }

    if (abortPrevious) {
      this.rowAbortController?.abort();
      this.rowAbortController = new AbortController();
    }

    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);

    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', 'global-quick-order-row');
    url.searchParams.set('_gqo', Date.now() + '-' + Math.random());

    const responseText = await this.fetchLookup(url, {text:true, notFound:true, signal: signal || (abortPrevious ? this.rowAbortController?.signal : null)});
    const row = this.parseServerRenderedRow(responseText);
    if (row.dataset.hasProduct !== 'true') throw this.requestError('not_found', 'Product is not present in the current catalog.');
    if (epoch !== this.contextEpoch) throw new Error('Customer context changed.');
    let rowKey;
    try { rowKey = this.contextFrom(row); }
    catch (error) { this.invalidateContext(error.message); throw error; }
    if (rowKey !== key) {
      this.invalidateContext('Product context changed. Verify selection again.');
      throw new Error('Product context changed.');
    }
    this.rowHtmlCache.set(cacheKey, row.outerHTML);

    return row;
  }

  parseServerRenderedRow(responseText) {
    const parsed = new DOMParser().parseFromString(responseText, 'text/html');
    const row = parsed.querySelector('[data-global-quick-order-row]');

    if (!row) {
      throw this.requestError('format', 'Unusable server-rendered row.');
    }

    return row;
  }

  attachRow(row, variantId, options = {}) {
    variantId = String(variantId);
    if (this.isSelectionLocked() || String(row.dataset.variantId) !== variantId) return false;
    if (this.selectedVariantIds.has(variantId)) {
      this.focusExistingRow(variantId);
      return false;
    }
    const { focus = true, initialQuantity = null } = options;

    this.initializeRowQuantity(row, initialQuantity);
    this.selectedVariantIds.add(variantId);
    this.emptyState?.setAttribute('hidden', '');

    row.querySelector('[data-global-quick-order-remove]')?.addEventListener('click', () => {
      if (this.isSelectionLocked()) return;
      this.selectedVariantIds.delete(variantId);
      row.remove();

      if (this.selectedVariantIds.size === 0) {
        this.emptyState?.removeAttribute('hidden');
      }

      this.updateSummary();
      this.setStatus('Selected row removed.');
    });

    this.selected.appendChild(row);
    row.tabIndex = -1;

    if (focus) {
      row.focus({ preventScroll: false });
    }
    return true;
  }

  async handleImportReview() {
    if (this.isSelectionLocked()) return;
    if (this.isImporting) {
      return;
    }

    this.cancelImportWork();
    const parsed = this.parseImportInput(this.importInput?.value || '');

    if (!parsed.ok) {
      this.importReviewRows = [];
      this.renderImportReview(0);
      this.setImportStatus(parsed.error, 'error');
      return;
    }

    await this.processBulkImportRows(parsed.rows, {
      sourceLabel: 'Source: Paste from Excel',
    });
  }

  async processBulkImportRows(rows, options = {}) {
    if (this.isSelectionLocked()) return;
    this.cancelImportWork();
    const run = this.importEpoch;
    const epoch = this.contextEpoch;
    const current = () => run === this.importEpoch && epoch === this.contextEpoch && !this.isSelectionLocked();
    this.importSourceLabel = options.sourceLabel || '';
    this.importProcessedCount = rows.length;
    const failureRow = (group, detail) => this.createImportReviewRow({
      identifiers: this.getUniqueImportIdentifiers(group), requestedQuantity: group.length === 1 ? group[0].requestedQuantityRaw : this.sumImportQuantities(group),
      finalQuantity: '—', ...detail, sourceCount: group.length
    });
    const invalid = rows.filter(row => row.status).map(row => failureRow([row], { status: row.status, note: row.note }));
    const groups = [...this.groupImportRowsByIdentifier(rows.filter(row => !row.status)).values()];
    const pending = groups.map(group => failureRow(group, {status:'Not processed', note:'Waiting for lookup.'}));
    this.importReviewRows = invalid.concat(pending);
    this.renderImportReview(rows.length);
    this.setImportProcessing(true);
    try {
      const entries = await this.mapWithConcurrency(groups, this.importConcurrency, async (group, index) => {
        if (!current()) return null;
        let result;
        if (!this.parseOrderQuantity(this.sumImportQuantities(group)).ok) result = {status:'error', detail:{status:'Invalid quantity',note:'Merged quantity exceeds the safe integer limit.'}};
        else {
          try { result = await this.resolveImportIdentifier(group[0].identifier); }
          catch (error) { result = {status:'error', detail:this.classifyImportError(error)}; }
        }
        if (!current()) return null;
        pending[index].note = result.status === 'matched' ? 'Waiting for server validation.' : 'Lookup complete.';
        return {group,result};
      });
      if (!current()) return;
      const reviewed = [...invalid];
      const variantGroups = this.groupImportRowsByResolvedVariant(entries.filter(Boolean), reviewed);
      const validation = [...variantGroups.values()];
      const waiting = validation.map(group => failureRow(group.rows,{status:'Not processed',note:'Waiting for server validation.'}));
      this.importReviewRows = reviewed.concat(waiting);
      this.renderImportReview(rows.length);
      await this.mapWithConcurrency(validation, this.importConcurrency, async (group,index) => {
        if (!current()) return;
        let result;
        try { result = await this.createValidatedImportReviewRow(group); }
        catch (error) { result = failureRow(group.rows,this.classifyImportError(error)); }
        if (!current()) return;
        result.sourceCount = group.rows.length;
        waiting[index] = result;
        this.importReviewRows = reviewed.concat(waiting);
        this.renderImportReview(rows.length);
      });
      if (!current()) return;
      const ready = this.getValidImportReviewRows().length;
      const failed = this.importReviewRows.filter(row => !row.canAdd && row.status !== 'Not processed').length;
      const unprocessed = this.importReviewRows.filter(row => row.status === 'Not processed').length;
      this.setImportStatus(ready + ' ready; ' + failed + ' failed/skipped; ' + unprocessed + ' not processed. Review every row before adding.',
        failed || unprocessed ? 'warning' : ready ? 'success' : 'error');
    } catch (error) {
      if (current()) this.setImportStatus('Review interrupted. Completed, failed and Not processed rows are shown. ' + error.message, 'error');
    } finally {
      if (run === this.importEpoch) this.setImportProcessing(false);
    }
  }

  downloadCsvTemplate() {
    const csvContent = '\uFEFFSKU,Quantity\r\n';
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8',
    });

    this.downloadBlob(blob, 'Atalliance-Quick-Order-Template.csv');
    this.setImportStatus('CSV template downloaded.', 'success');
  }

  async downloadXlsxTemplate() {
    if (!this.templateXlsxButton || this.templateXlsxButton.disabled) {
      return;
    }

    this.setTemplateXlsxLoading(true);
    this.setImportStatus('Generating Excel template...', 'loading');

    try {
      const XLSX = await this.loadXlsxLibrary();
      const workbook = XLSX.utils.book_new();
      const orderSheet = XLSX.utils.aoa_to_sheet([['SKU', 'Quantity']]);
      const instructionsSheet = XLSX.utils.aoa_to_sheet([
        ['Atalliance Quick Order Template'],
        [''],
        ['1. Enter the original SKU/EAN as Text in Identifier. Numeric/formula identifier cells are rejected; do not rely on display formatting to preserve zeros.'],
        ['CSV has no text column formatting: set the identifier column to Text before entering data in Excel.'],
        ['The 100-row limit excludes the first complete header and blank rows; duplicates and invalid rows still count.'],
        ['2. Enter a positive safe whole number. Blank and zero quantities are invalid.'],
        ['3. Do not change the column names.'],
        ['4. One product/variant per row.'],
        [`5. Maximum ${this.importMaxRows} order rows per import.`],
        ['6. Quantity rules and volume pricing are validated automatically when the file is reviewed.'],
        ['7. Save the file as .xlsx before uploading.'],
        [''],
        ['Important: The Order worksheet must remain the first worksheet because Global Quick Order imports the first worksheet only.'],
      ]);

      // Materialise blank text cells: column metadata alone is not a reliable Excel number format.
      for (let row = 2; row <= this.importMaxRows + 1; row += 1) orderSheet['A' + row] = {t:'s',v:'',z:'@'};
      orderSheet['!ref'] = 'A1:B' + (this.importMaxRows + 1);
      orderSheet['!cols'] = [{ wch: 28 }, { wch: 12 }];
      orderSheet['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
      orderSheet.A1.s = { font: { bold: true } };
      orderSheet.B1.s = { font: { bold: true } };
      instructionsSheet['!cols'] = [{ wch: 110 }];

      XLSX.utils.book_append_sheet(workbook, orderSheet, 'Order');
      XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');

      const workbookData = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true,
      });
      const blob = new Blob([workbookData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      this.downloadBlob(blob, 'Atalliance-Quick-Order-Template.xlsx');
      this.setImportStatus('Excel template downloaded.', 'success');
    } catch (error) {
      this.setImportStatus('Excel template could not be generated. Please try again or download the CSV template.', 'error');
    } finally {
      this.setTemplateXlsxLoading(false);
    }
  }

  setTemplateXlsxLoading(isLoading) {
    if (!this.templateXlsxButton) {
      return;
    }

    this.isTemplateXlsxLoading = isLoading;
    this.templateXlsxButton.disabled = this.isSelectionLocked() || isLoading;
    this.templateXlsxButton.classList.toggle('btn--loading', isLoading);
    this.templateXlsxButton.toggleAttribute('aria-busy', isLoading);
  }

  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async handleCsvFileSelection(event) {
    if (this.isSelectionLocked()) return;
    this.cancelImportWork();
    const fileRun = this.fileEpoch;
    const fileContext = this.contextEpoch;
    const fileCurrent = () => fileRun === this.fileEpoch && fileContext === this.contextEpoch && !this.isSelectionLocked();
    const file = event.target.files?.[0] || null;

    this.csvFile = null;
    this.csvFileText = '';
    this.csvFileRows = [];
    this.csvFileType = '';
    this.csvSheetName = '';
    this.csvDetectedRows = 0;
    this.updateCsvReviewButtonState();

    if (!file) {
      this.clearCsvMetadata();
      return;
    }

    const fileType = this.getOrderFileType(file);

    if (!fileType) {
      this.clearCsvMetadata();
      this.setImportStatus('Choose a .csv or .xlsx file.', 'error');
      return;
    }

    if (fileType === 'csv' && file.size > this.csvMaxFileSize) {
      this.clearCsvMetadata();
      this.setImportStatus('CSV file must be smaller than 1 MB.', 'error');
      return;
    }

    if (fileType === 'xlsx' && file.size > this.xlsxMaxFileSize) {
      this.clearCsvMetadata();
      this.setImportStatus('Excel file must be smaller than 5 MB.', 'error');
      return;
    }

    this.isReadingFile = true;
    this.updateAddAllButtonState();
    try {
      this.setImportStatus(fileType === 'xlsx' ? 'Loading Excel file support...' : 'Reading order file...', 'loading');

      const parsed = fileType === 'xlsx' ? await this.parseXlsxImportFile(file) : await this.parseCsvImportFile(file);

      if (!fileCurrent()) return;
      if (!parsed.ok) {
        this.clearCsvMetadata();
        this.setImportStatus(parsed.error, 'error');
        return;
      }

      this.csvFile = file;
      this.csvFileText = parsed.rawText || '';
      this.csvFileRows = parsed.rows;
      this.csvFileType = fileType;
      this.csvSheetName = parsed.sheetName || '';
      this.csvDetectedRows = parsed.rows.length;
      this.renderCsvMetadata(file.name, this.csvDetectedRows, fileType, this.csvSheetName);
      this.setImportStatus('', 'idle');
      this.updateCsvReviewButtonState();
    } catch (error) {
      if (!fileCurrent()) return;
      this.clearCsvMetadata();
      const detail = error.code ? this.classifyImportError(error) : {status:'Format error',note:fileType === 'xlsx' ? 'Excel file could not be read.' : 'CSV file could not be read.'};
      this.setImportStatus(detail.status + ': ' + detail.note, 'error');
    } finally {
      if (fileRun === this.fileEpoch) {
        this.isReadingFile = false;
        this.updateAddAllButtonState();
      }
    }
  }

  async handleCsvImportReview() {
    if (this.isSelectionLocked()) return;
    if (this.isImporting) {
      return;
    }

    if (!this.csvFile || this.csvFileRows.length === 0) {
      this.setImportStatus('Choose a CSV or Excel file first.', 'error');
      return;
    }

    const sourceType = this.csvFileType === 'xlsx' ? 'Excel' : 'CSV';
    const sheetLabel = this.csvSheetName ? ` - ${this.csvSheetName}` : '';

    await this.processBulkImportRows(this.csvFileRows, {
      sourceLabel: `Source: ${sourceType} - ${this.csvFile.name}${sheetLabel}`,
    });
  }

  clearCsvFile() {
    if (this.isSelectionLocked()) return;
    this.cancelImportWork();
    this.csvFile = null;
    this.csvFileText = '';
    this.csvFileRows = [];
    this.csvFileType = '';
    this.csvSheetName = '';
    this.csvDetectedRows = 0;

    if (this.csvInput) {
      this.csvInput.value = '';
    }

    this.clearCsvMetadata();
    this.setImportStatus('', 'idle');
    this.updateCsvReviewButtonState();
  }

  clearCsvMetadata() {
    if (this.csvMeta) {
      this.csvMeta.textContent = '';
      this.csvMeta.setAttribute('hidden', '');
    }

    this.csvRemoveButton?.setAttribute('hidden', '');
  }

  renderCsvMetadata(fileName, rowCount, fileType = 'csv', sheetName = '') {
    if (!this.csvMeta) {
      return;
    }

    const fileTypeLabel = fileType === 'xlsx' ? 'Excel' : 'CSV';
    const sheetLabel = sheetName ? ` - ${sheetName}` : '';

    this.csvMeta.textContent = `${fileName} - ${fileTypeLabel}${sheetLabel} - ${rowCount} row${rowCount === 1 ? '' : 's'} detected`;
    this.csvMeta.removeAttribute('hidden');
    this.csvRemoveButton?.removeAttribute('hidden');
  }

  updateCsvReviewButtonState() {
    if (!this.csvReviewButton) {
      return;
    }

    this.csvReviewButton.disabled = this.isSelectionLocked() || this.isImporting || !this.csvFile || this.csvFileRows.length === 0;
  }

  getOrderFileType(file) {
    const fileName = file.name || '';

    if (/\.csv$/i.test(fileName)) {
      return 'csv';
    }

    if (/\.xlsx$/i.test(fileName)) {
      return 'xlsx';
    }

    return '';
  }

  async parseCsvImportFile(file) {
    const isWorkbook = await this.fileAppearsToBeXlsx(file);

    if (isWorkbook) {
      return {
        ok: false,
        error: 'This file appears to be an Excel workbook, not a CSV file. Please save or rename it as .xlsx and upload again.',
      };
    }

    const text = await this.readCsvFileText(file);
    const parsed = this.parseCsvImportText(text);

    return parsed.ok ? { ...parsed, rawText: text } : parsed;
  }

  async fileAppearsToBeXlsx(file) {
    const signature = new Uint8Array(await file.slice(0, 4).arrayBuffer());

    return signature[0] === 0x50 && signature[1] === 0x4B;
  }

  async readCsvFileText(file) {
    if (file.text) {
      return file.text();
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.addEventListener('load', () => resolve(String(reader.result || '')));
      reader.addEventListener('error', () => reject(new Error('unreadable file')));
      reader.readAsText(file);
    });
  }

  async parseXlsxImportFile(file) {
    const XLSX = await this.loadXlsxLibrary();
    const workbook = XLSX.read(await file.arrayBuffer(), {
      type: 'array',
      cellText: true,
      cellFormula: true,
      cellNF: true,
      cellHTML: false,
    });
    const sheetName = workbook.SheetNames?.[0] || '';

    if (!sheetName || !workbook.Sheets?.[sheetName]) {
      return { ok: false, error: 'No usable worksheet found in this Excel file.' };
    }

    let rows;
    try { rows = this.getXlsxWorksheetRows(workbook.Sheets[sheetName], XLSX); }
    catch (error) { return {ok:false,error:'Format error: ' + error.message}; }
    const parsed = this.parseTabularImportRows(rows);

    return parsed.ok ? { ...parsed, sheetName } : parsed;
  }

  getXlsxWorksheetRows(sheet, XLSX) {
    // Visit populated cells, never scan an Excel !ref rectangle with millions of empty rows.
    const populated = Object.keys(sheet).filter(address => /^[A-Z]+[1-9]\d*$/.test(address)).map(address => ({
      position: XLSX.utils.decode_cell(address), cell: sheet[address]
    })).filter(({ cell }) => cell && (String(cell.v ?? '').trim() !== '' || cell.f));
    if (!populated.length) return [];
    const firstColumn = populated.reduce((min, entry) => Math.min(min, entry.position.c), Infinity);
    const rowMap = new Map();
    for (const {position, cell} of populated) {
      if (!rowMap.has(position.r)) rowMap.set(position.r, new Map());
      rowMap.get(position.r).set(position.c, cell);
      if (rowMap.size > this.importMaxRows + 1) throw new Error('Maximum ' + this.importMaxRows + ' non-empty data rows before consolidation; no rows were imported.');
    }
    return [...rowMap.entries()].sort((a,b) => a[0]-b[0]).map(([, cells]) => {
      const width = Math.max(...cells.keys()) - firstColumn + 1;
      // Extra populated columns are reported, but do not allocate arbitrary worksheet widths.
      return Array.from({length: Math.max(2, Math.min(width, 3))}, (_, offset) => {
        const cell = cells.get(firstColumn + offset);
        if (!cell) return '';
        const numericOrFormula = cell.t !== 's' && cell.t !== 'str' || Boolean(cell.f);
        return { gqoCell: true, text: this.getXlsxCellText(cell),
          identifierRisk: numericOrFormula ? 'Excel identifier is not plain text (number, formula or other typed cell). Leading zeros or numeric precision may already be lost. Re-enter the original identifier as Text; no reconstruction was attempted.' : '',
          quantityRisk: cell.f || !['s','str','n'].includes(cell.t) ? 'Quantity formulas and non-text/non-number cells are not supported. Provide a plain positive integer.' : cell.t === 'n' && String(Math.abs(cell.v)).replace(/[^0-9]/g, '').length > 15 ? 'Excel numeric quantity may have lost precision. Re-enter the original whole number as Text.' : '' };
      });
    });
  }

  getXlsxCellText(cell) {
    return cell?.v === undefined ? '' : String(cell.v);
  }

  async loadXlsxLibrary() {
    if (window.XLSX) return window.XLSX;
    if (this.xlsxLoadPromise) return this.xlsxLoadPromise;
    if (!this.xlsxScriptUrl) throw this.requestError('service', 'Excel support asset is missing. Use CSV or contact the store.');
    this.xlsxLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const cleanup = () => { window.clearTimeout(timer); script.onload = null; script.onerror = null; };
      const timer = window.setTimeout(() => {
        cleanup(); script.remove();
        reject(this.requestError('timeout', 'Excel support request timed out. Try again or use CSV.'));
      }, 20000);
      script.src = this.xlsxScriptUrl;
      script.async = true;
      script.onload = () => {
        cleanup();
        if (window.XLSX) resolve(window.XLSX);
        else reject(this.requestError('service', 'Excel support asset did not initialise.'));
      };
      script.onerror = () => {
        cleanup(); script.remove();
        reject(this.requestError('network', 'Excel support could not be loaded. Try again or use CSV.'));
      };
      document.head.appendChild(script);
    }).catch(error => { this.xlsxLoadPromise = null; throw error; });
    return this.xlsxLoadPromise;
  }

  parseCsvImportText(rawText) {
    const text = String(rawText || '').replace(/^\uFEFF/, '');

    if (text.trim().length === 0) {
      return { ok: false, error: 'CSV is empty.' };
    }

    let records;

    try {
      const delimiter = this.detectCsvDelimiter(text);
      records = this.stripCsvSepDirective(this.parseCsvRecords(text, delimiter));
    } catch (error) {
      return { ok: false, error: 'Invalid CSV structure.' };
    }

    const nonEmptyRecords = records.filter((record) => record.some((cell) => String(cell).trim().length > 0));

    if (nonEmptyRecords.length === 0) {
      return { ok: false, error: 'CSV is empty.' };
    }

    return this.parseTabularImportRows(nonEmptyRecords);
  }

  parseTabularImportRows(records) {
    const text = cell => this.cleanImportCell(cell?.gqoCell ? cell.text : cell ?? '');
    const usable = records.filter(record => Array.from(record || []).some(cell => text(cell) !== ''));
    if (!usable.length) return { ok: false, error: 'No order rows found.' };
    const header = this.getImportHeaderIndexes(usable[0].map(text));
    const hasHeader = header.hasAnyHeader;
    const data = hasHeader ? usable.slice(1) : usable;
    if (!data.length) return { ok: false, error: 'Only a header was provided. Add order rows.' };
    if (data.length > this.importMaxRows) return { ok: false, error: 'Maximum ' + this.importMaxRows + ' non-empty data rows before duplicate consolidation; the first complete header is excluded. Nothing was imported.' };
    const identifierIndex = hasHeader ? header.identifierIndex : 0;
    const quantityIndex = hasHeader ? header.quantityIndex : 1;
    const rows = data.map((record, index) => {
      const identifierCell = record[identifierIndex];
      const quantityCell = record[quantityIndex];
      const row = this.createBulkImportInputRow(text(identifierCell), text(quantityCell), index + 1 + Number(hasHeader));
      if (record.length !== 2) { row.status = 'Format error'; row.note = 'Exactly two columns are supported: Identifier and Quantity (or a complete reversed header).'; }
      else if (identifierCell?.identifierRisk) { row.status = 'Identifier needs text'; row.note = identifierCell.identifierRisk; }
      else if (quantityCell?.quantityRisk) { row.status = 'Invalid quantity'; row.note = quantityCell.quantityRisk; }
      return row;
    });
    return { ok: true, rows };
  }

  detectCsvDelimiter(text) {
    const meaningfulLines = String(text)
      .replace(/^\uFEFF/, '')
      .split(/\r\n|\n|\r/)
      .map((line) => line.replace(/\r/g, ''))
      .filter(line => line.trim().length > 0)
      .slice(0, 5);
    const sepDirective = this.getCsvSepDirective(meaningfulLines[0] || '');

    if (sepDirective) {
      return sepDirective;
    }

    const candidates = [',', ';', '\t'];
    const scores = candidates.map((delimiter) => {
      let score = 0;

      meaningfulLines.forEach((line) => {
        try {
          const columnCount = this.parseCsvRecords(line, delimiter)[0]?.length || 0;

          if (columnCount > 1) {
            score += columnCount;
          }
        } catch (error) {
          score -= 1;
        }
      });

      return { delimiter, score };
    });

    scores.sort((first, second) => second.score - first.score);

    return scores[0]?.score > 0 ? scores[0].delimiter : ',';
  }

  getCsvSepDirective(line) {
    const normalizedLine = String(line || '').replace(/^\uFEFF/, '').trim().toLowerCase();
    const match = normalizedLine.match(/^sep=(,|;|\\t|\t)$/);

    if (!match) {
      return '';
    }

    return match[1] === '\\t' ? '\t' : match[1];
  }

  stripCsvSepDirective(records) {
    if (records.length === 0) {
      return records;
    }

    const firstRecordCells = records[0].map((cell) => this.normalizeHeaderCell(cell));
    const firstRecordText = firstRecordCells.join('');

    return (firstRecordCells.length === 2 && firstRecordCells[0] === 'sep=' && firstRecordCells[1] === '') || (firstRecordCells.length === 1 && /^sep=(,|;|\\t|\t)$/.test(firstRecordText)) ? records.slice(1) : records;
  }

  parseCsvRecords(text, delimiter = ',') {
    const records = [];
    let record = [];
    let cell = '';
    let isQuoted = false;
    let quoteClosed = false;

    for (let index = 0; index < text.length; index += 1) {
      const char = text[index];
      const nextChar = text[index + 1];

      if (isQuoted) {
        if (char === '"' && nextChar === '"') {
          cell += '"';
          index += 1;
        } else if (char === '"') {
          isQuoted = false;
          quoteClosed = true;
        } else {
          cell += char;
        }
        continue;
      }

      if (char === '"' && cell.trim().length === 0 && !quoteClosed) {
        isQuoted = true;
        cell = '';
        continue;
      }

      if (char === '"') throw new Error('invalid csv quote');

      if (char === delimiter) {
        record.push(this.cleanImportCell(cell));
        cell = '';
        quoteClosed = false;
        continue;
      }

      if (char === '\r' || char === '\n') {
        record.push(this.cleanImportCell(cell));
        records.push(record);
        record = [];
        cell = '';
        quoteClosed = false;

        if (char === '\r' && nextChar === '\n') {
          index += 1;
        }

        continue;
      }

      if (quoteClosed && char.trim().length > 0) {
        throw new Error('invalid csv');
      }

      cell += char;
    }

    if (isQuoted) {
      throw new Error('invalid csv');
    }

    if (cell.length > 0 || record.length > 0) {
      record.push(this.cleanImportCell(cell));
      records.push(record);
    }

    return records;
  }

  parseImportInput(rawText) {
    const text = String(rawText ?? '').replace(/^\uFEFF/, '');
    if (!text.trim()) return { ok: false, error: 'Paste Identifier and Quantity rows first.' };
    try {
      if (/[\t,;]/.test(text)) return this.parseCsvImportText(text);
      return this.parseTabularImportRows(text.split(/\r\n|\r|\n/).map(line => this.splitImportColumns(line)));
    } catch (error) { return { ok: false, error: 'Format error: ' + error.message }; }
  }

  splitImportColumns(line) {
    const text = String(line).trim();
    if (!text) return [];
    const columns = text.split(/\s{2,}/);
    if (columns.length > 1) return columns;
    const pair = text.match(/^(.*?)\s+(\S+)$/);
    return pair ? [pair[1], pair[2]] : [text];
  }

  cleanImportCell(value) {
    return String(value ?? '').replace(/^\uFEFF/, '').replace(/\r/g, '').trim();
  }

  normalizeHeaderCell(value) {
    return this.cleanImportCell(value)
      .replace(/^\uFEFF/, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\r/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  isImportHeader(columns) {
    const header = this.getImportHeaderIndexes(columns);

    return header.hasAnyHeader;
  }

  getImportHeaderIndexes(columns) {
    const identifiers = new Set(['sku', 'ean', 'barcode', 'identifier', 'product code', 'item code']);
    const quantities = new Set(['qty', 'quantity']);
    const names = columns.map(cell => this.normalizeHeaderCell(cell));
    const identifierIndex = names.findIndex(name => identifiers.has(name));
    const quantityIndex = names.findIndex(name => quantities.has(name));
    // A single header-like SKU is data, never a reason to discard a product.
    return { identifierIndex, quantityIndex, hasAnyHeader: columns.length === 2 && identifierIndex !== -1 && quantityIndex !== -1 && identifierIndex !== quantityIndex };
  }

  createBulkImportInputRow(identifier, requestedQuantityRaw, lineNumber) {
    const parsed = this.parseOrderQuantity(requestedQuantityRaw);
    let status = !identifier ? 'Format error' : parsed.ok ? '' : 'Invalid quantity';
    let note = !identifier ? 'Identifier is required.' : parsed.ok ? '' : parsed.error;
    if (/^[+-]?\d+(?:\.\d+)?e[+-]?\d+$/i.test(identifier)) {
      status = 'Identifier needs text';
      note = 'Scientific notation may have lost identifier digits. Supply the original SKU/EAN as text; no digits were reconstructed.';
    }
    return { lineNumber, identifier: String(identifier), requestedQuantityRaw, requestedQuantity: parsed.ok ? parsed.value : NaN, status, note };
  }

  groupImportRowsByIdentifier(rows) {
    return rows.reduce((groups, row) => {
      const key = this.normalizeMatchValue(row.identifier);

      if (!groups.has(key)) {
        groups.set(key, []);
      }

      groups.get(key).push(row);
      return groups;
    }, new Map());
  }

  groupImportRowsByResolvedVariant(entries, reviewRows) {
    const groups = new Map();
    for (const {group,result} of entries) {
      const total = this.sumImportQuantities(group);
      if (result.status !== 'matched') {
        const detail = result.status === 'error' ? result.detail : result.status === 'multiple'
          ? {status:'Multiple matches',note:'More than one exact SKU/EAN match.'}
          : {status:'Not found',note:'No exact match in current catalog search results.'};
        reviewRows.push(this.createImportReviewRow({identifiers:this.getUniqueImportIdentifiers(group),
          requestedQuantity:total,finalQuantity:'—',sourceCount:group.length,...detail}));
        continue;
      }
      const id = String(result.match.variant.id);
      if (!groups.has(id)) groups.set(id,{variantId:id,match:result.match,rows:[]});
      groups.get(id).rows.push(...group);
    }
    return groups;
  }

  async resolveImportIdentifier(identifier) {
    const cacheKey = this.contextKey + ':' + this.contextEpoch + ':' + this.normalizeMatchValue(identifier);

    if (this.identifierResolutionCache.has(cacheKey)) {
      return this.identifierResolutionCache.get(cacheKey);
    }

    const request = this.resolveImportIdentifierUncached(identifier).catch((error) => {
      this.identifierResolutionCache.delete(cacheKey);
      throw error;
    });

    this.identifierResolutionCache.set(cacheKey, request);
    return request;
  }

  async resolveImportIdentifierUncached(identifier) {
    const products = await this.searchProducts(identifier, { abortPrevious: false });
    const matches = [];
    const seenVariantIds = new Set();

    for (const product of products) {
      const handle = this.getProductHandle(product);

      if (!handle) {
        continue;
      }

      const productJson = await this.fetchProductMetadata(handle, { abortPrevious: false });
      const variants = this.findExactImportIdentifierMatches(productJson.variants || [], identifier);

      variants.forEach((variant) => {
        const variantId = String(variant.id);

        if (seenVariantIds.has(variantId)) {
          return;
        }

        seenVariantIds.add(variantId);
        matches.push({
          product: productJson,
          variant,
          handle: productJson.handle || handle,
        });
      });
    }

    if (matches.length === 0) {
      return { status: 'not_found' };
    }

    if (matches.length > 1) {
      return { status: 'multiple', matches };
    }

    return { status: 'matched', match: matches[0] };
  }

  findExactImportIdentifierMatches(variants, identifier) {
    const trimmedIdentifier = String(identifier).trim();
    const normalizedSku = this.normalizeMatchValue(trimmedIdentifier);

    return variants.filter((variant) => {
      const skuMatches = variant.sku && this.normalizeMatchValue(variant.sku) === normalizedSku;
      const barcodeMatches = variant.barcode && String(variant.barcode).trim() === trimmedIdentifier;

      return skuMatches || barcodeMatches;
    });
  }

  async createValidatedImportReviewRow(group) {
    const variantId = String(group.variantId);
    const identifiers = this.getUniqueImportIdentifiers(group.rows);
    const requestedQuantity = this.sumImportQuantities(group.rows);
    if (!this.parseOrderQuantity(requestedQuantity).ok) return this.createImportReviewRow({identifiers,requestedQuantity:'Invalid total',finalQuantity:'—',status:'Invalid quantity',note:'Merged quantity must be a positive safe integer.'});

    if (this.selectedVariantIds.has(variantId)) {
      return this.createImportReviewRow({
        identifiers,
        requestedQuantity: this.formatImportQuantity(requestedQuantity),
        matchedText: this.getImportMatchText(group.match),
        finalQuantity: '—',
        status: 'Already in order',
        note: group.rows.length > 1 ? 'Duplicate rows consolidated' : '',
      });
    }

    const row = await this.fetchServerRenderedRow(group.match.handle, variantId, { abortPrevious: false });

    if (row.dataset.hasProduct !== 'true') {
      return this.createImportReviewRow({
        identifiers,
        requestedQuantity: this.formatImportQuantity(requestedQuantity),
        matchedText: this.getImportMatchText(group.match),
        finalQuantity: '—',
        status: 'Not found',
      });
    }

    const matchedText = this.getServerRenderedRowMatchText(row);
    const orderRule = this.formatOrderRule(row);

    if (row.dataset.available !== 'true') {
      return this.createImportReviewRow({
        identifiers,
        requestedQuantity: this.formatImportQuantity(requestedQuantity),
        matchedText,
        orderRule,
        finalQuantity: '—',
        status: 'Unavailable',
        note: group.rows.length > 1 ? 'Duplicate rows consolidated' : '',
      });
    }

    const finalQuantity = this.normalizeManualQuantity(requestedQuantity, row);
    const quantityError = this.validateDelta(row, finalQuantity);
    if (quantityError) return this.createImportReviewRow({identifiers,requestedQuantity,matchedText,orderRule,finalQuantity:'—',status:'Invalid quantity',note:quantityError});
    const status = finalQuantity === requestedQuantity ? 'Ready' : 'Adjusted';
    const ruleNote = status === 'Adjusted'
      ? `${this.formatImportQuantity(requestedQuantity)} adjusted to ${this.formatImportQuantity(finalQuantity)} to meet ${orderRule}.`
      : '';

    return this.createImportReviewRow({
      identifiers,
      requestedQuantity: this.formatImportQuantity(requestedQuantity),
      matchedText,
      orderRule,
      finalQuantity,
      status,
      note: group.rows.length > 1 ? 'Duplicate rows consolidated' : '',
      ruleNote,
      row,
      variantId,
      canAdd: status === 'Ready' || status === 'Adjusted',
    });
  }

  createImportReviewRow(options) {
    return {
      identifiers: options.identifiers || [],
      requestedQuantity: options.requestedQuantity,
      matchedText: options.matchedText || '—',
      orderRule: options.orderRule || '—',
      finalQuantity: options.finalQuantity,
      status: options.status,
      note: options.note || '',
      ruleNote: options.ruleNote || '',
      row: options.row || null,
      variantId: options.variantId ? String(options.variantId) : '',
      canAdd: Boolean(options.canAdd),
      sourceCount: options.sourceCount || 1,
    };
  }

  renderImportReview(processedCount = 0) {
    if (!this.importReviewPanel || !this.importResults || !this.importSummary) {
      return;
    }

    this.importReviewPanel.toggleAttribute('hidden', this.importReviewRows.length === 0);
    this.importResults.innerHTML = '';
    this.importSummary.innerHTML = '';

    if (this.importSource) {
      this.importSource.textContent = this.importSourceLabel;
      this.importSource.toggleAttribute('hidden', !this.importSourceLabel);
    }

    if (this.importReviewRows.length === 0) {
      this.updateImportAddButtonState();
      return;
    }

    const summary = this.getImportSummary(processedCount);
    const summaryList = document.createElement('p');

    summaryList.textContent = summary.join(' · ');
    this.importSummary.appendChild(summaryList);

    const table = document.createElement('div');
    table.className = 'global-quick-order-import-table';

    const head = document.createElement('div');
    head.className = 'global-quick-order-import-table__head';
    ['Identifier', 'Requested Qty', 'Matched Product / Variant', 'Order Rule', 'Final Qty', 'Status'].forEach((label) => {
      const cell = document.createElement('span');
      cell.textContent = label;
      head.appendChild(cell);
    });
    table.appendChild(head);

    this.importReviewRows.forEach((reviewRow) => {
      const row = document.createElement('div');
      row.className = 'global-quick-order-import-table__row';

      [
        ['Identifier', reviewRow.identifiers.join(', ') || '—'],
        ['Requested Qty', this.formatImportQuantity(reviewRow.requestedQuantity)],
        ['Matched Product / Variant', reviewRow.matchedText || '—'],
        ['Order Rule', reviewRow.orderRule || '—'],
        ['Final Qty', this.formatImportQuantity(reviewRow.finalQuantity)],
      ].forEach(([label, value]) => {
        const cell = document.createElement('span');
        cell.dataset.label = label;
        cell.textContent = value;

        if (label === 'Order Rule' && reviewRow.ruleNote) {
          const ruleNote = document.createElement('small');
          ruleNote.textContent = reviewRow.ruleNote;
          cell.appendChild(ruleNote);
        }

        row.appendChild(cell);
      });

      const statusCell = document.createElement('span');
      const status = document.createElement('span');

      statusCell.dataset.label = 'Status';
      status.className = 'global-quick-order-import-status-badge';
      status.dataset.status = this.getImportStatusKey(reviewRow.status);
      status.textContent = reviewRow.status;
      statusCell.appendChild(status);

      if (reviewRow.note) {
        const note = document.createElement('small');
        note.textContent = reviewRow.note;
        statusCell.appendChild(note);
      }

      row.appendChild(statusCell);
      table.appendChild(row);
    });

    this.importResults.appendChild(table);
    this.updateImportAddButtonState();
  }

  getImportSummary(processedCount) {
    const counts = new Map();
    for (const row of this.importReviewRows) counts.set(row.status,(counts.get(row.status)||0)+1);
    return [processedCount + ' source data rows (before consolidation)', ...[...counts].map(([status,count]) => count + ' ' + status)];
  }

  addImportRowsToOrder() {
    if (this.isSelectionLocked()) return;
    const validRows = this.getValidImportReviewRows();
    let addedCount = 0;

    validRows.forEach((reviewRow) => {
      if (!reviewRow.row || !reviewRow.variantId) {
        return;
      }

      if (this.selectedVariantIds.has(reviewRow.variantId)) {
        reviewRow.status = 'Already in order';
        reviewRow.canAdd = false;
        reviewRow.finalQuantity = '—';
        return;
      }

      if (!this.attachRow(reviewRow.row, reviewRow.variantId, {
        focus: false,
        initialQuantity: reviewRow.finalQuantity,
      })) {
        reviewRow.status = 'Could not validate';
        reviewRow.canAdd = false;
        return;
      }
      reviewRow.status = 'Already in order';
      reviewRow.canAdd = false;
      reviewRow.row = null;
      addedCount += 1;
    });

    this.hideSuccessActions();
    this.updateSummary();
    this.renderImportReview(this.importProcessedCount);
    const remaining = this.importReviewRows.filter(row => row.status !== 'Already in order').length;
    this.setImportStatus(
      (addedCount > 0 ? addedCount + ' items added to Selected Items.' : 'No new valid items to add.') + ' ' + remaining + ' failed or not processed; review the status of each row.',
      remaining ? 'warning' : addedCount > 0 ? 'success' : 'error'
    );
  }

  clearImport() {
    if (this.isSelectionLocked()) return;
    this.cancelImportWork();
    if (this.importInput) {
      this.importInput.value = '';
    }

    this.importReviewRows = [];
    this.importProcessedCount = 0;
    this.importSourceLabel = '';
    this.setImportStatus('', 'idle');
    this.renderImportReview(0);
  }

  getValidImportReviewRows() {
    return this.importReviewRows.filter((row) => row.canAdd && (row.status === 'Ready' || row.status === 'Adjusted'));
  }

  updateImportAddButtonState() {
    if (!this.importAddButton) {
      return;
    }

    this.importAddButton.disabled = this.isSelectionLocked() || this.isImporting || this.getValidImportReviewRows().length === 0;
  }

  setImportProcessing(isProcessing) {
    this.isImporting = isProcessing;
    this.updateAddAllButtonState();

    if (this.importReviewButton) {
      this.importReviewButton.disabled = isProcessing;
      this.importReviewButton.classList.toggle('btn--loading', isProcessing);
      this.importReviewButton.toggleAttribute('aria-busy', isProcessing);
    }

    this.updateImportAddButtonState();
    this.updateCsvReviewButtonState();
  }

  setImportStatus(message, type = 'info') {
    if (!this.importStatus) {
      return;
    }

    this.importStatus.textContent = message;
    this.importStatus.dataset.status = type;
  }

  getImportStatusKey(status) {
    return String(status).toLowerCase().replace(/\s+/g, '-');
  }

  async mapWithConcurrency(items, limit, callback) {
    const results = new Array(items.length);
    let nextIndex = 0;

    const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (nextIndex < items.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        results[currentIndex] = await callback(items[currentIndex], currentIndex);
      }
    });

    await Promise.all(workers);
    return results;
  }

  sumImportQuantities(rows) {
    let total = 0;
    for (const row of rows) {
      const parsed = this.parseOrderQuantity(row.requestedQuantity);
      if (!parsed.ok || !Number.isSafeInteger(total + parsed.value)) return NaN;
      total += parsed.value;
    }
    return total;
  }

  getUniqueImportIdentifiers(rows) {
    return [...new Set(rows.map((row) => row.identifier).filter(Boolean))];
  }

  getImportMatchText(match) {
    if (!match) {
      return '—';
    }

    return [match.product?.title, match.variant?.title].filter(Boolean).join(' / ') || '—';
  }

  getServerRenderedRowMatchText(row) {
    return [row.dataset.productTitle, row.dataset.variantTitle].filter(Boolean).join(' / ') || this.getImportMatchText(null);
  }

  formatImportQuantity(value) {
    return value === undefined || value === null || value === '' ? '—' : Number.isNaN(value) ? 'Invalid total' : String(value);
  }

  parseOrderQuantity(raw) {
    const text = String(raw ?? '').trim();
    if (!/^\d+$/.test(text)) return { ok: false, error: 'Enter a positive whole number using digits only; blank, fractions and decimals are not allowed.' };
    const value = Number(text);
    if (!Number.isSafeInteger(value) || value < 1) return { ok: false, error: 'Quantity must be between 1 and ' + Number.MAX_SAFE_INTEGER + '; zero is not an order quantity.' };
    return { ok: true, value };
  }

  cancelImportWork() {
    this.importEpoch = (this.importEpoch || 0) + 1;
    this.fileEpoch = (this.fileEpoch || 0) + 1;
    this.isReadingFile = false;
    this.setImportProcessing(false);
    this.importReviewRows = [];
    this.importProcessedCount = 0;
    this.renderImportReview(0);
  }

  requestError(code, message) {
    const error = new Error(message);
    error.code = code;
    if (code === 'cancelled') error.name = 'AbortError';
    return error;
  }

  classifyImportError(error) {
    const labels = { network: 'Network failure', timeout: 'Timeout', service: 'Service error',
      not_found: 'Not found', format: 'Format error', cancelled: 'Not processed' };
    return { status: labels[error.code] || (error.name === 'AbortError' ? 'Not processed' : 'Could not validate'), note: error.message || 'Request could not be verified.' };
  }

  async fetchLookup(url, options = {}) {
    const controller = new AbortController();
    let timedOut = false;
    const abort = () => controller.abort();
    if (options.signal?.aborted) abort();
    options.signal?.addEventListener('abort', abort, { once: true });
    const timer = window.setTimeout(() => { timedOut = true; controller.abort(); }, 20000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw this.requestError(response.status === 404 && options.notFound ? 'not_found' : 'service', 'Shopify returned HTTP ' + response.status + '.');
      try { return options.text ? await response.text() : await response.json(); }
      catch (error) {
        if (controller.signal.aborted) throw error;
        throw this.requestError('format', 'Shopify returned an unusable response.');
      }
    } catch (error) {
      if (timedOut) throw this.requestError('timeout', 'Request timed out. No absence of a product has been established.');
      if (controller.signal.aborted) throw this.requestError('cancelled', 'Request cancelled; item not processed.');
      if (error.code) throw error;
      throw this.requestError('network', 'Network request failed. Check your connection and review again.');
    } finally {
      window.clearTimeout(timer);
      options.signal?.removeEventListener('abort', abort);
    }
  }

  invalidateSearch() {
    this.searchEpoch = (this.searchEpoch || 0) + 1;
    this.productChoiceEpoch = (this.productChoiceEpoch || 0) + 1;
    this.selectionEpoch += 1;
    this.searchAbortController?.abort();
    this.productAbortController?.abort();
    this.rowAbortController?.abort();
    this.pendingVariantRequests.clear();
    this.clearResults();
    this.hideResultsPanel();
  }

  contextFrom(root) {
    const element = root.querySelector('[data-gqo-context]');
    let value;
    try { value = JSON.parse(element?.textContent || ''); } catch (_) { throw new Error('Customer context could not be verified.'); }
    if (!value.customer) throw new Error('Sign in with your B2B account, then verify selection.');
    if (value.b2b !== true) throw new Error('Quick Order requires a B2B customer account.');
    if (!value.company || !value.location || value.locationAllowed !== true) {
      throw new Error('Choose an authorised Company Location in your account, then verify selection.');
    }
    if (![value.customer, value.company, value.location].every(id => /^\d+$/.test(String(id))) ||
        !value.currency || !value.country || !value.language || !value.market) {
      throw new Error('Customer, Company Location or market context could not be verified.');
    }
    return JSON.stringify([String(value.customer), String(value.company), String(value.location),
      value.currency, value.country, value.language, String(value.market)]);
  }

  async fetchContext() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', 'global-quick-order-context');
    url.searchParams.delete('sections');
    url.searchParams.set('_gqo', Date.now() + '-' + Math.random());
    const html = await this.fetchFreshHtml(url);
    return this.contextFrom(new DOMParser().parseFromString(html, 'text/html'));
  }

  async fetchFreshHtml(url) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Could not verify current Shopify data.');
      return await response.text();
    } finally { window.clearTimeout(timeout); }
  }

  invalidateContext(message) {
    this.invalidateSearch();
    this.cancelImportWork();
    this.contextReady = false;
    this.contextEpoch += 1;
    this.searchCache.clear();
    this.productMetadataCache.clear();
    this.identifierResolutionCache.clear();
    this.rowHtmlCache.clear();
    this.importReviewRows = [];
    this.renderImportReview(0);
    this.clearResults();
    this.lockSelection();
    this.selected.querySelectorAll('[data-global-quick-order-row]').forEach(row => {
      row.dataset.validationError = 'Verify selection before adding.';
      row.querySelectorAll('.global-quick-order-row__tiers, .global-quick-order-row__price s').forEach(element => { element.hidden = true; });
      row.querySelectorAll('[data-global-quick-order-unit-price], [data-global-quick-order-line-total], [data-gqo-rule-note], [data-gqo-quantity-note]')
        .forEach(element => { element.textContent = 'Not verified'; });
    });
    this.subtotal.textContent = 'Not verified';
    if (!this.cartOutcomePending && !this.isAddingToCart) this.setCartStatus(message, 'warning');
    this.updateAddAllButtonState();
  }

  async monitorContext() {
    if (!this.isConnected || document.hidden || this.contextChecking || this.isAddingToCart || this.cartOutcomePending) return;
    const epoch = this.contextEpoch;
    try {
      const key = await this.fetchContext();
      if (epoch !== this.contextEpoch) return;
      if (key !== this.contextKey || !this.contextReady) await this.verifySelectionContext();
    } catch (error) {
      if (epoch === this.contextEpoch) this.invalidateContext(error.message + ' Use Verify Selection to recover.');
    }
  }

  async verifySelectionContext() {
    if (this.contextChecking || this.isAddingToCart || this.cartOutcomePending) return;
    this.contextChecking = true;
    this.invalidateContext('Verifying customer, Company Location, cart and selected items...');
    const epoch = this.contextEpoch;
    this.contextVerifyButton.disabled = true;
    try {
      const key = await this.fetchContext();
      const cart = await this.fetchCartJson();
      this.snapshotCart(cart);
      await this.revalidateRows(key, cart, epoch);
      if (await this.fetchContext() !== key || epoch !== this.contextEpoch) throw new Error('Context changed during verification.');
      this.contextKey = key;
      this.contextReady = true;
      this.cartForRules = cart;
      this.selected.querySelectorAll('[data-global-quick-order-row]').forEach(row => this.updateRowPricing(row, Number(row.dataset.quantity)));
      this.setCartStatus(this.hasInvalidRows()
        ? 'Selection verified, but some quantities or cart lines need review. Nothing has been submitted.'
        : 'B2B context verified. Quantities are amounts to add; prices are estimates. Review your selection before adding.', 'info');
    } catch (error) {
      this.contextReady = false;
      this.setCartStatus(error.message + ' No items were submitted. Use Verify Selection after resolving this.', 'error');
    } finally {
      this.contextChecking = false;
      this.contextVerifyButton.disabled = false;
      if (this.contextReady && !this.cartOutcomePending) this.unlockSelection();
      this.updateSummary();
    }
  }

  async freshRow(row, key) {
    const root = window.Shopify?.routes?.root || '/';
    const handle = row.dataset.productHandle;
    if (!handle) throw new Error('Selected product handle is missing; remove and select it again.');
    const url = new URL(root + 'products/' + encodeURIComponent(handle), window.location.origin);
    url.searchParams.set('variant', row.dataset.variantId);
    url.searchParams.set('section_id', 'global-quick-order-row');
    url.searchParams.set('_gqo', Date.now() + '-' + Math.random());
    const fresh = this.parseServerRenderedRow(await this.fetchFreshHtml(url));
    let rowKey;
    try { rowKey = this.contextFrom(fresh); }
    catch (error) { this.invalidateContext(error.message); throw error; }
    if (fresh.dataset.variantId !== row.dataset.variantId || fresh.dataset.hasProduct !== 'true' ||
        rowKey !== key) {
      this.invalidateContext('Product or customer context changed.');
      throw new Error('Product or customer context changed. Verify selection again.');
    }
    const min = Number(fresh.dataset.quantityMin);
    const step = Number(fresh.dataset.quantityIncrement);
    const max = fresh.dataset.quantityMax === '' ? null : Number(fresh.dataset.quantityMax);
    if (!Number.isSafeInteger(min) || min < 1 || !Number.isSafeInteger(step) || step < 1 || min % step !== 0 ||
        (max !== null && (!Number.isSafeInteger(max) || max < min)) ||
        !Number.isSafeInteger(Number(fresh.dataset.basePriceCents)) || Number(fresh.dataset.basePriceCents) < 0) {
      throw new Error('Shopify quantity or price data is not usable. Review this variant in View Cart.');
    }
    return fresh;
  }

  async revalidateRows(key, cart, epoch) {
    const rows = Array.from(this.selected.querySelectorAll('[data-global-quick-order-row]'));
    // Stage every response before applying any prices; never mix locations.
    const fresh = await this.mapWithConcurrency(rows, 4, row => this.freshRow(row, key));
    if (epoch !== this.contextEpoch) throw new Error('Context changed during product verification.');
    rows.forEach((row, index) => {
      const next = fresh[index];
      ['quantityMin', 'quantityMax', 'quantityIncrement', 'basePriceCents', 'available'].forEach(name => {
        row.dataset[name] = next.dataset[name] ?? '';
      });
      row.querySelector('.global-quick-order-row__tiers')?.remove();
      const tiers = next.querySelector('.global-quick-order-row__tiers');
      if (tiers) { const copy = tiers.cloneNode(true); copy.hidden = true; row.appendChild(copy); }
      row.querySelector('.global-quick-order-row__price s')?.remove();
      const compare = next.querySelector('.global-quick-order-row__price s');
      if (compare) { const copy = compare.cloneNode(true); copy.hidden = true; row.querySelector('.global-quick-order-row__price')?.appendChild(copy); }
      row.querySelectorAll('[data-gqo-context]').forEach(element => element.remove());
      row.appendChild(next.querySelector('[data-gqo-context]').cloneNode(true));
    });
    if (JSON.parse(key)[3] !== cart.currency) throw new Error('Cart currency does not match the verified context.');
    this.cartForRules = cart;
  }

  async verifyBeforeSubmission(attempt) {
    const epoch = this.contextEpoch;
    let key;
    try { key = await this.fetchContext(); }
    catch (error) { this.invalidateContext(error.message); throw error; }
    if (!this.contextReady || key !== this.contextKey) {
      this.invalidateContext('Customer context changed. Verify selection again.');
      throw new Error('Customer context changed. Use Verify Selection before adding.');
    }
    const cart = await this.fetchCartJson();
    attempt.before = this.snapshotCart(cart);
    await this.revalidateRows(key, cart, epoch);
    let finalKey;
    try { finalKey = await this.fetchContext(); }
    catch (error) { this.invalidateContext(error.message); throw error; }
    if (finalKey !== key || epoch !== this.contextEpoch) {
      this.invalidateContext('Context changed during verification.');
      throw new Error('Context changed during verification. Use Verify Selection.');
    }
    for (const item of attempt.selection) {
      this.updateRowPricing(item.element, item.quantity);
      if (item.element.dataset.validationError) throw new Error(item.label + ': ' + item.element.dataset.validationError);
    }
    attempt.contextKey = key;
  }

  cartQuantityFor(row) {
    if (!this.cartForRules) return { quantity: 0, error: 'Cart quantities are not verified.' };
    const lines = this.cartForRules.items.filter(item => String(item.variant_id ?? item.id) === row.dataset.variantId);
    // Ajax add does not identify a target line key. Do not guess which differently
    // priced/property/subscription/bundle line receives the delta or price break.
    if (lines.length > 1 || lines.some(item => Object.keys(item.properties || {}).length ||
        item.selling_plan_allocation || item.parent_relationship || item.item_components?.length)) {
      return { quantity: lines.reduce((sum, item) => sum + item.quantity, 0),
        error: 'This variant has split, customised, subscription or bundle cart lines. Review it in View Cart; Quick Order cannot safely estimate or add it.' };
    }
    return { quantity: lines[0]?.quantity || 0, error: '' };
  }

  getDeltaRules(row) {
    const rules = this.getQuantityRules(row);
    const existing = this.cartQuantityFor(row);
    // Valid totals are multiples of increment and must satisfy the total MOQ.
    const firstTotal = Math.ceil(Math.max(rules.min, existing.quantity + 1) / rules.increment) * rules.increment;
    return { ...rules, min: firstTotal - existing.quantity,
      max: rules.max === null ? null : Math.floor(rules.max / rules.increment) * rules.increment - existing.quantity,
      existing: existing.quantity, error: existing.error };
  }

  validateDelta(row, quantity) {
    const rules = this.getQuantityRules(row);
    const existing = this.cartQuantityFor(row);
    const total = existing.quantity + quantity;
    if (existing.error) return existing.error;
    if (row.dataset.available !== 'true') return 'This variant is not available in the current context.';
    if (!Number.isSafeInteger(quantity) || quantity < 1) return 'Enter a positive whole number to add.';
    if (!Number.isSafeInteger(total)) return 'After-add quantity exceeds the safe integer limit.';
    if (total < rules.min || total % rules.increment !== 0 || (rules.max !== null && total > rules.max)) {
      return 'After-add quantity ' + total + ' must meet Min ' + rules.min + ', Step ' + rules.increment +
        (rules.max === null ? '' : ', Max ' + rules.max) + '. Edit the quantity to add.';
    }
    return '';
  }

  hasInvalidRows() {
    return Array.from(this.selected.querySelectorAll('[data-global-quick-order-row]'))
      .some(row => Boolean(row.dataset.validationError));
  }


  isSelectionLocked() {
    return this.isAddingToCart || this.cartOutcomePending || this.contextReady === false || this.contextChecking;
  }

  selectionRequestIsCurrent(epoch) {
    return epoch === this.selectionEpoch && !this.isSelectionLocked();
  }

  lockSelection() {
    // Abort cannot invalidate continuations of promises that already resolved.
    this.selectionEpoch += 1;
    this.searchAbortController?.abort();
    this.productAbortController?.abort();
    this.rowAbortController?.abort();
    this.pendingVariantRequests.clear();
    [this.entryControls, this.selected].filter(Boolean).forEach((region) => {
      region.inert = true;
      region.querySelectorAll('button, input, textarea, select').forEach((control) => {
        if (!this.lockedControls.has(control)) this.lockedControls.set(control, control.disabled);
        control.disabled = true;
      });
    });
  }

  unlockSelection() {
    if (this.isSelectionLocked()) return;
    [this.entryControls, this.selected].filter(Boolean).forEach((region) => {
      region.inert = false;
    });
    this.lockedControls.forEach((disabled, control) => {
      if (control.isConnected) control.disabled = disabled;
    });
    this.lockedControls.clear();
    this.setTemplateXlsxLoading(Boolean(this.isTemplateXlsxLoading));
    this.updateImportAddButtonState();
    this.updateCsvReviewButtonState();
    this.selected.querySelectorAll('[data-global-quick-order-row]').forEach((row) => {
      this.updateQuantityButtonStates(row, Number(row.dataset.quantity));
    });
  }

  captureSubmission() {
    const rows = Array.from(this.selected.querySelectorAll('[data-global-quick-order-row][data-has-product="true"]'));
    const items = this.collectCartItems();
    if (items.length !== rows.length || new Set(items.map((item) => item.id)).size !== items.length) {
      throw new Error('Review the selected variants before submitting.');
    }
    return Object.freeze(rows.map((element, index) => Object.freeze({
      element,
      id: String(items[index].id),
      quantity: items[index].quantity,
      label: element.dataset.sku || element.dataset.productTitle || String(items[index].id),
    })));
  }

  async addAllToCart() {
    if (this.isSelectionLocked() || this.isImporting || this.isReadingFile || this.selectedVariantIds.size === 0) return;
    this.setAddAllLoading(true);
    this.lockSelection();
    this.hideSuccessActions();
    this.cartReviewActions?.setAttribute('hidden', '');
    this.cartReviewDetails?.replaceChildren();
    this.setCartStatus('Checking the cart before adding items...', 'loading');
    const attempt = { postStarted: false, confirmed: false };

    try {
      attempt.selection = this.captureSubmission();
      if (attempt.selection.length === 0) throw new Error('No selected items to add.');
      attempt.items = Object.freeze(attempt.selection.map(({ id, quantity }) => Object.freeze({ id: Number(id), quantity })));
      await this.verifyBeforeSubmission(attempt);
      attempt.sections = this.getGroupedSections();
      this.cartAttempt = attempt;
      this.setCartStatus('Adding items to cart. Please wait...', 'loading');
      attempt.postStarted = true;
      const parsedState = await this.submitCartAdd(attempt.items, attempt.sections);
      if (!this.isConfirmedCartAdd(parsedState, attempt.items)) {
        throw new Error('The add response could not confirm the submitted items.');
      }
      attempt.confirmed = true;
      // Consume the acknowledged snapshot BEFORE any fallible UI refresh.
      this.removeSubmittedRows(attempt.selection);
      this.cartAttempt = null;
      this.showSuccessActions();
      this.querySelector('[data-gqo-cart-total]').textContent = '';

      try {
        await this.refreshCartAfterAdd(parsedState, attempt.sections);
        this.setCartStatus('Items added to cart.', 'success');
      } catch (error) {
        this.setCartStatus('Items added to cart, but the cart display could not refresh. Use View Cart to check your items. Do not add this batch again.', 'warning');
      }
    } catch (error) {
      if (!attempt.postStarted) {
        this.cartAttempt = null;
        this.setCartStatus('Nothing was submitted. ' + this.getSafeErrorMessage(error) + ' Please try again.', 'error');
      } else if (attempt.confirmed) {
        // Even a selected-list UI failure must not turn an acknowledged POST into a retry.
        this.cartOutcomePending = true;
        this.cartAttempt = attempt;
        this.cartReviewActions?.removeAttribute('hidden');
        this.setCartStatus('Items added to cart, but the selected list could not refresh. View Cart before clearing the submitted selection. Do not add this batch again.', 'warning');
      } else {
        // A rejected/aborted request can still have changed the cart.
        this.cartOutcomePending = true;
        this.cartAttempt = attempt;
        await this.reconcileCartAttempt();
      }
    } finally {
      this.setAddAllLoading(false);
      if (!this.cartOutcomePending) this.unlockSelection();
      this.updateSummary();
    }
  }

  isConfirmedCartAdd(state, items) {
    if (!state || state.status || !Array.isArray(state.items)) return false;
    const returned = new Map();
    for (const item of state.items) {
      const id = String(item.variant_id ?? item.id);
      if (!Number.isSafeInteger(item.quantity) || item.quantity < 1) return false;
      returned.set(id, (returned.get(id) || 0) + item.quantity);
    }
    // Shopify returns cumulative line quantities, not necessarily the delta.
    return items.every((item) => (returned.get(String(item.id)) || 0) >= item.quantity);
  }

  snapshotCart(cart) {
    if (!cart || !Array.isArray(cart.items) || typeof cart.token !== 'string' || !cart.token) {
      throw new Error('Could not read a reliable cart snapshot.');
    }
    const quantities = new Map();
    for (const item of cart.items) {
      const id = String(item.variant_id ?? item.id);
      if (!/^\d+$/.test(id) || !Number.isSafeInteger(item.quantity) || item.quantity < 0) {
        throw new Error('Could not read a reliable cart snapshot.');
      }
      quantities.set(id, (quantities.get(id) || 0) + item.quantity);
    }
    return { token: cart.token, currency: cart.currency, quantities };
  }

  compareCartSnapshots(attempt, after) {
    const sameCart = attempt.before.token === after.token && attempt.before.currency === after.currency;
    return {
      sameCart,
      rows: attempt.selection.map(({ id, label, quantity }) => ({
        id, label, requested: quantity,
        before: attempt.before.quantities.get(id) || 0,
        after: after.quantities.get(id) || 0,
      })),
    };
  }

  async reconcileCartAttempt() {
    this.setCartStatus(this.cartAttempt.confirmed ? 'Items already added. Checking current cart quantities...' : 'The add result is not confirmed. Checking current cart quantities...', 'loading');
    this.cartReviewDetails?.replaceChildren();
    this.cartReviewActions?.removeAttribute('hidden');
    this.cartRecheckButton.disabled = true;
    this.cartReviewedButton.disabled = true;
    try {
      const cart = await this.fetchCartJson();
      const comparison = this.compareCartSnapshots(this.cartAttempt, this.snapshotCart(cart));
      const currentContext = await this.fetchContext();
      comparison.sameContext = currentContext === this.cartAttempt.contextKey;
      if (!comparison.sameContext) this.invalidateContext('Customer context changed during submission.');
      comparison.sameCart = comparison.sameCart && comparison.sameContext;
      this.cartAttempt.observed = comparison;
      this.renderCartComparison(comparison);
      this.setCartStatus(
        this.cartAttempt.confirmed
          ? 'Items already added to cart. The current totals below are for reference. View Cart before clearing the submitted selection; do not add this batch again.'
          : comparison.sameCart
          ? 'Result needs checking. The quantities below are observed cart totals, not confirmation of what this request added. Another tab may have changed the cart, or this request may still finish. Automatic retry is blocked. View Cart before clearing this selection.'
          : 'Result needs checking. The cart or currency changed during the request, or the customer/Company Location no longer matches, so quantities cannot be reconciled reliably. Automatic retry is blocked. View Cart before clearing this selection.',
        'warning'
      );
    } catch (error) {
      this.setCartStatus(this.cartAttempt.confirmed
        ? 'Items already added to cart, but the cart display could not refresh. View Cart before clearing the submitted selection; do not add this batch again.'
        : 'Result needs checking. Current cart quantities could not be read. Some items may already be added. Automatic retry is blocked. Use View Cart or Check Cart Again; do not resend this batch.', 'warning');
    } finally {
      this.cartRecheckButton.disabled = false;
      this.cartReviewedButton.disabled = false;
    }
  }

  renderCartComparison(comparison) {
    if (!this.cartReviewDetails) return;
    const list = document.createElement('ul');
    comparison.rows.forEach((item) => {
      const line = document.createElement('li');
      const delta = item.after - item.before;
      line.textContent = item.label + ': requested +' + item.requested + '; cart before ' + item.before + '; cart now ' + item.after +
        (comparison.sameCart ? '; observed change ' + (delta >= 0 ? '+' : '') + delta : ' (different cart/context)') + '.';
      list.appendChild(line);
    });
    this.cartReviewDetails.replaceChildren(list);
  }

  async recheckCartOutcome() {
    if (!this.cartOutcomePending || this.isAddingToCart || !this.cartAttempt) return;
    this.setAddAllLoading(true);
    try {
      // Read only: even an exact delta cannot prove causality across tabs.
      await this.reconcileCartAttempt();
    } finally {
      this.setAddAllLoading(false);
    }
  }

  clearReviewedSubmission() {
    if (!this.cartOutcomePending || this.isAddingToCart || !this.cartAttempt) return;
    // Explicit acknowledgement discards the held selection; never requeues it.
    this.removeSubmittedRows(this.cartAttempt.selection);
    this.cartAttempt = null;
    this.cartOutcomePending = false;
    this.cartReviewActions?.setAttribute('hidden', '');
    this.cartReviewDetails?.replaceChildren();
    this.unlockSelection();
    this.updateSummary();
    this.invalidateContext('Submitted selection cleared. No cart items were changed or retried. Use Verify Selection, then select only quantities still needed after checking your cart.');
  }

  removeSubmittedRows(selection) {
    selection.forEach(({ element, id }) => {
      if (element.parentElement === this.selected && element.dataset.variantId === id) element.remove();
    });
    this.selectedVariantIds = new Set(Array.from(this.selected.querySelectorAll('[data-global-quick-order-row]')).map((row) => row.dataset.variantId));
    this.emptyState?.toggleAttribute('hidden', this.selectedVariantIds.size > 0);
    this.updateSummary();
  }

  setCartStatus(message, type = 'info') {
    if (!this.cartStatus) return;
    this.cartStatus.textContent = message;
    this.cartStatus.dataset.status = type;
  }

  async refreshCartAfterAdd(parsedState, sectionIds) {
    const cart = await this.fetchCartJson();
    this.snapshotCart(cart);
    this.cartForRules = cart;
    if (sectionIds.length > 0) {
      let sections = parsedState.sections;
      if (!sections || !this.hasUsableSections(sections, sectionIds)) {
        const url = new URL(FoxTheme.routes.root_url, window.location.origin);
        url.searchParams.set('sections', sectionIds.join(','));
        sections = await this.fetchCartResponse(url, { method: 'GET' });
      }
      if (!sections || !this.hasUsableSections(sections, sectionIds)) throw new Error('The cart display could not refresh.');
      cart.sections = sections;
    }
    this.publishCartUpdate(cart);
    this.dispatchProductAddedEvent(parsedState);
    this.openCartDrawer();
    const total = this.querySelector('[data-gqo-cart-total]');
    if (total && Number.isSafeInteger(cart.total_price)) {
      total.textContent = 'Shopify cart total at last refresh: ' + cart.currency + ' ' + this.formatMoney(cart.total_price) + '. View Cart for the latest final price.';
    }
  }

  collectCartItems() {
    return Array.from(this.selected.querySelectorAll('[data-global-quick-order-row][data-has-product="true"]'))
      .map((row) => {
        const input = row.querySelector('[data-global-quick-order-quantity-input]');
        const parsed = this.parseOrderQuantity(input ? input.value : row.dataset.quantity);
        if (!parsed.ok) throw new Error((row.dataset.sku || 'Selected item') + ': ' + parsed.error);
        const quantity = parsed.value;
        this.updateRowPricing(row, quantity);
        return { id: this.parsePositiveInteger(row.dataset.variantId, 0), quantity };
      })
      .filter((item) => item.id > 0 && item.quantity > 0);
  }

  getGroupedSections() {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );
    return [...new Set(sectionsToBundle.filter(Boolean))];
  }

  async fetchCartResponse(url, options) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      const state = await response.json().catch(() => null);
      if (!response.ok || state?.status || !state) {
        throw new Error(state?.description || state?.message || 'Could not complete cart request (' + response.status + ').');
      }
      return state;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async submitCartAdd(items, sectionsToBundle) {
    return this.fetchCartResponse(FoxTheme.routes.cart_add_url, {
      ...FoxTheme.utils.fetchConfig('javascript'),
      headers: {
        ...FoxTheme.utils.fetchConfig('javascript').headers,
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      }),
    });
  }

  async fetchCartJson() {
    return this.fetchCartResponse(FoxTheme.routes.cart_url + '.js', {
      ...FoxTheme.utils.fetchConfig('json', 'GET'),
    });
  }

  hasUsableSections(sections, sectionIds) {
    return sectionIds.every((sectionId) => {
      const html = sections[sectionId];

      if (typeof html !== 'string' || html.trim().length === 0) {
        return false;
      }

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const drawer = document.querySelector('cart-drawer');
      if (drawer && FoxTheme.utils.getSectionId(drawer) === sectionId) {
        return ['CartDrawerBody-', 'CartDrawerFooter-', 'CartDrawerEmpty-'].every((prefix) => parsed.getElementById(prefix + sectionId));
      }
      return Boolean(parsed.body && parsed.body.innerHTML.trim().length > 0);
    });
  }

  publishCartUpdate(cartJson) {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      cart: cartJson,
      source: 'global-quick-order',
    });
  }

  dispatchProductAddedEvent(parsedState) {
    document.dispatchEvent(
      new CustomEvent('product-ajax:added', {
        detail: {
          product: parsedState,
        },
      })
    );
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');

    if (FoxTheme.settings?.cartType !== 'page') {
      if (!cartDrawer?.show) throw new Error('The cart drawer is not ready.');
      cartDrawer.show(this.addAllButton);
    }
  }

  clearSelectedItems() {
    this.selected.querySelectorAll('[data-global-quick-order-row]').forEach((row) => row.remove());
    this.selectedVariantIds.clear();
    this.emptyState?.removeAttribute('hidden');
    this.updateSummary();
  }

  setAddAllLoading(isLoading) {
    this.isAddingToCart = isLoading;

    if (!this.addAllButton) {
      return;
    }

    this.addAllButton.classList.toggle('btn--loading', isLoading);
    this.addAllButton.toggleAttribute('aria-busy', isLoading);
    this.updateAddAllButtonState();
  }

  updateAddAllButtonState() {
    const busy = Boolean(this.isAddingToCart || this.cartOutcomePending || this.contextChecking);
    if (this.contextVerifyButton) this.contextVerifyButton.disabled = busy;
    const discard = this.querySelector('[data-gqo-discard]');
    if (discard) discard.disabled = busy;
    // Context recovery is only actionable when verification is required and no submission is held.
    const contextActions = this.querySelector('[data-gqo-context-actions]');
    if (contextActions) contextActions.hidden = this.contextReady !== false || busy;
    if (!this.addAllButton) {
      return;
    }

    const isDisabled = this.isSelectionLocked() || this.isImporting || this.isReadingFile || this.selectedVariantIds.size === 0 || this.hasInvalidRows();
    this.addAllButton.disabled = isDisabled;
    this.addAllButton.setAttribute('aria-disabled', String(isDisabled));
  }

  showSuccessActions() {
    this.successActions?.removeAttribute('hidden');
  }

  hideSuccessActions() {
    this.successActions?.setAttribute('hidden', '');
  }

  initializeRowQuantity(row, initialQuantity = null) {
    const input = row.querySelector('[data-global-quick-order-quantity-input]');
    const minus = row.querySelector('[data-global-quick-order-quantity-minus]');
    const plus = row.querySelector('[data-global-quick-order-quantity-plus]');
    const rules = this.getDeltaRules(row);
    const normalizedInitialQuantity = initialQuantity === null ? this.normalizeQuantity(rules.min, row) : this.normalizeManualQuantity(initialQuantity, row);

    if (!input) {
      return;
    }

    input.value = normalizedInitialQuantity;
    this.updateRowPricing(row, normalizedInitialQuantity);

    minus?.addEventListener('click', () => {
      if (this.isSelectionLocked() || !this.parseOrderQuantity(input.value).ok) return;
      const nextQuantity = this.normalizeQuantity(this.getInputQuantity(input) - this.getDeltaRules(row).increment, row);
      input.value = nextQuantity;
      this.updateRowPricing(row, nextQuantity);
      this.updateSummary();
    });

    plus?.addEventListener('click', () => {
      if (this.isSelectionLocked() || !this.parseOrderQuantity(input.value).ok) return;
      const nextQuantity = this.normalizeQuantity(this.getInputQuantity(input) + this.getDeltaRules(row).increment, row);
      input.value = nextQuantity;
      this.updateRowPricing(row, nextQuantity);
      this.updateSummary();
    });

    input.addEventListener('change', () => {
      if (this.isSelectionLocked()) {
        input.value = row.dataset.quantity;
        return;
      }
      const nextQuantity = this.normalizeManualQuantity(input.value, row);
      input.value = nextQuantity;
      this.updateRowPricing(row, nextQuantity);
      this.updateSummary();
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        input.blur();
      }
    });
  }

  getInputQuantity(input) {
    const parsed = Number.parseInt(input.value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  getQuantityRules(row) {
    const min = this.parsePositiveInteger(row.dataset.quantityMin, 1);
    const increment = this.parsePositiveInteger(row.dataset.quantityIncrement, 1);
    const rawMax = Number.parseInt(row.dataset.quantityMax, 10);
    const max = Number.isFinite(rawMax) && rawMax >= min ? rawMax : null;

    return { min, increment, max };
  }

  formatOrderRule(row) {
    const rules = this.getQuantityRules(row);
    const parts = [`Min ${rules.min}`, `Step ${rules.increment}`];

    if (rules.max !== null) {
      parts.push(`Max ${rules.max}`);
    }

    return parts.join(' · ');
  }

  normalizeQuantity(value, row) {
    return this.normalizeDelta(value, row, Math.round);
  }

  normalizeManualQuantity(value, row) {
    return this.normalizeDelta(value, row, Math.ceil);
  }

  normalizeDelta(value, row, round) {
    const rules = this.getDeltaRules(row);
    const check = this.parseOrderQuantity(value);
    if (!check.ok) return NaN;
    const parsed = check.value;
    if (rules.error || (rules.max !== null && rules.max < rules.min)) return Number.isFinite(parsed) && parsed > 0 ? parsed : rules.min;
    const steps = Number.isFinite(parsed) ? Math.max(0, round((parsed - rules.min) / rules.increment)) : 0;
    const quantity = rules.min + steps * rules.increment;
    return rules.max === null ? quantity : Math.min(quantity, rules.max);
  }

  updateRowPricing(row, quantity) {
    const existing = this.cartQuantityFor(row);
    const rules = this.getDeltaRules(row);
    const validQuantity = this.parseOrderQuantity(quantity).ok;
    const totalQuantity = validQuantity ? existing.quantity + quantity : NaN;
    const error = this.validateDelta(row, quantity);
    row.dataset.validationError = error;
    row.querySelectorAll('.global-quick-order-row__tiers, .global-quick-order-row__price s').forEach(element => { element.hidden = !this.contextReady || Boolean(error); });
    row.dataset.cartQuantity = String(existing.quantity);
    row.dataset.afterQuantity = String(totalQuantity);
    const unitPriceCents = this.getEffectiveUnitPriceCents(row, totalQuantity);
    const lineTotalCents = validQuantity ? unitPriceCents * quantity : 0;
    const input = row.querySelector('[data-global-quick-order-quantity-input]');
    const unitPrice = row.querySelector('[data-global-quick-order-unit-price]');
    const lineTotal = row.querySelector('[data-global-quick-order-line-total]');

    row.dataset.quantity = validQuantity ? String(quantity) : '';
    row.dataset.effectiveUnitPriceCents = String(unitPriceCents);
    row.dataset.lineTotalCents = String(lineTotalCents);

    if (input) {
      if (validQuantity) input.value = quantity;
      input.min = rules.min;
      input.step = rules.increment;
      if (rules.max === null) input.removeAttribute('max');
      else input.max = Math.max(0, rules.max);
      input.setAttribute('aria-invalid', String(Boolean(error)));
    }
    let note = row.querySelector('[data-gqo-quantity-note]');
    if (!note) {
      note = document.createElement('small');
      note.dataset.gqoQuantityNote = '';
      (row.querySelector('.global-quick-order-row__quantity') || row).appendChild(note);
    }
    const ruleNote = row.querySelector('[data-gqo-rule-note]');
    if (ruleNote) ruleNote.textContent = 'After-add rules: ' + this.formatOrderRule(row);
    const availability = row.querySelector('[data-gqo-availability]');
    if (availability) availability.textContent = row.dataset.available === 'true' ? 'Available' : 'Unavailable';
    note.textContent = 'In cart: ' + existing.quantity + '; adding: ' + (validQuantity ? quantity : 'invalid') + '; after add: ' + (validQuantity ? totalQuantity : 'not calculated') + '. ' + error;

    if (unitPrice) {
      unitPrice.textContent = error ? 'Not verified' : this.formatMoney(unitPriceCents);
    }

    if (lineTotal) {
      lineTotal.textContent = error ? 'Not verified' : this.formatMoney(lineTotalCents);
    }

    this.updateQuantityButtonStates(row, quantity);
  }

  getEffectiveUnitPriceCents(row, quantity) {
    let unitPriceCents = this.parseNonNegativeInteger(row.dataset.basePriceCents, 0);
    let appliedThreshold = 0;

    row.querySelectorAll('[data-global-quick-order-price-break]').forEach((priceBreak) => {
      const minimumQuantity = this.parsePositiveInteger(priceBreak.dataset.minQuantity, 0);
      const priceCents = this.parseNonNegativeInteger(priceBreak.dataset.priceCents, unitPriceCents);

      if (minimumQuantity <= quantity && minimumQuantity >= appliedThreshold) {
        appliedThreshold = minimumQuantity;
        unitPriceCents = priceCents;
      }
    });

    return unitPriceCents;
  }

  updateQuantityButtonStates(row, quantity) {
    const rules = this.getDeltaRules(row);
    const minus = row.querySelector('[data-global-quick-order-quantity-minus]');
    const plus = row.querySelector('[data-global-quick-order-quantity-plus]');

    if (minus) {
      minus.disabled = this.isSelectionLocked() || quantity <= rules.min;
    }

    if (plus) {
      plus.disabled = this.isSelectionLocked() || (rules.max !== null && quantity >= rules.max);
    }
  }

  updateSummary() {
    const rows = Array.from(this.selected.querySelectorAll('[data-global-quick-order-row][data-has-product="true"]'));
    const totals = rows.reduce(
      (summary, row) => {
        const quantity = this.parseNonNegativeInteger(row.dataset.quantity, 0);
        const lineTotalCents = this.parseNonNegativeInteger(row.dataset.lineTotalCents, 0);

        summary.units += quantity;
        summary.subtotalCents += lineTotalCents;
        return summary;
      },
      { units: 0, subtotalCents: 0 }
    );

    this.skuCount.textContent = String(rows.length);
    this.unitCount.textContent = String(totals.units);
    this.subtotal.textContent = this.contextReady === false || this.hasInvalidRows() ? 'Not verified' : (this.contextKey ? JSON.parse(this.contextKey)[3] + ' ' : '') + this.formatMoney(totals.subtotalCents);
    this.classList.toggle('has-selected-items', rows.length > 0);
    this.updateAddAllButtonState();
  }

  focusExistingRow(variantId) {
    const row = this.selected.querySelector(`[data-variant-id="${CSS.escape(variantId)}"]`);

    if (!row) {
      return;
    }

    row.classList.add('is-duplicate-focus');
    row.tabIndex = -1;
    row.focus({ preventScroll: false });
    window.setTimeout(() => row.classList.remove('is-duplicate-focus'), 1400);
  }

  findExactSkuOrBarcodeMatches(variants, query) {
    const normalizedQuery = this.normalizeMatchValue(query);

    if (!normalizedQuery) {
      return [];
    }

    return variants.filter((variant) => {
      return (
        this.normalizeMatchValue(variant.sku || '') === normalizedQuery ||
        this.normalizeMatchValue(variant.barcode || '') === normalizedQuery
      );
    });
  }

  getProductHandle(product) {
    if (product.handle) {
      return product.handle;
    }

    const url = product.url || product.online_store_url || '';
    const parts = url.split('?')[0].split('/').filter(Boolean);
    const productIndex = parts.indexOf('products');

    return productIndex !== -1 ? parts[productIndex + 1] : parts.pop();
  }

  normalizeMatchValue(value) {
    return String(value).trim().toLowerCase();
  }

  parsePositiveInteger(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  parseNonNegativeInteger(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  formatMoney(cents) {
    if (window.FoxTheme?.Currency?.formatMoney) {
      return window.FoxTheme.Currency.formatMoney(cents, this.moneyFormat);
    }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'AUD',
    }).format(cents / 100);
  }

  getSafeErrorMessage(error) {
    return error?.message || 'The cart request could not be completed.';
  }

  clearResults() {
    this.results.innerHTML = '';
  }

  showResultsPanel() {
    this.resultsPanel?.removeAttribute('hidden');
  }

  hideResultsPanel() {
    this.resultsPanel?.setAttribute('hidden', '');
  }

  setStatus(message) {
    if (this.isSelectionLocked()) return;
    this.status.textContent = message;
    this.status.dataset.status = this.getStatusType(message);
  }

  getStatusType(message) {
    const normalizedMessage = String(message).toLowerCase();

    if (!normalizedMessage) {
      return 'idle';
    }

    if (normalizedMessage.includes('failed') || normalizedMessage.includes('error') || normalizedMessage.includes('unavailable')) {
      return 'error';
    }

    if (normalizedMessage.includes('added') || normalizedMessage.includes('loaded')) {
      return 'success';
    }

    if (
      normalizedMessage.includes('searching') ||
      normalizedMessage.includes('loading') ||
      normalizedMessage.includes('fetching') ||
      normalizedMessage.includes('adding')
    ) {
      return 'loading';
    }

    if (normalizedMessage.includes('no search results') || normalizedMessage.includes('no matching')) {
      return 'empty';
    }

    return 'info';
  }

  debounce(callback, wait) {
    let timeout;

    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback.apply(this, args), wait);
    };
  }
}

customElements.define('global-quick-order', GlobalQuickOrder);
